export type ContractStatus = 'draft' | 'active' | 'completed' | 'terminated';
export type DeliverableStatus = 'pending' | 'in-progress' | 'completed' | 'overdue';
export type MilestonePayStatus = 'upcoming' | 'reached' | 'missed';
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue';
export type ClientType = 'public' | 'private' | 'ngo' | 'international';
export type ServiceType = 'management' | 'policy' | 'engineering' | 'other';

export interface ContractDeliverable {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: DeliverableStatus;
}

export interface ContractMilestone {
  id: string;
  title: string;
  date: string;
  paymentAmount?: number;
  status: MilestonePayStatus;
  description?: string;
}

export interface ContractInvoice {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  amount: number;
  currency: 'USD' | 'EUR' | 'TRY';
  status: InvoiceStatus;
  pdfUrl: string;
  description: string;
}

export interface Contract {
  id: string;
  projectId: string;
  assignedConsultantIds?: string[];
  contractNumber: string;
  title: string;
  clientName: string;
  clientType: ClientType;
  serviceType: ServiceType;
  status: ContractStatus;
  contractValue: number;
  currency: 'USD' | 'EUR' | 'TRY';
  startDate: string;
  endDate: string;
  staffCountRequired: number;
  deliverables: ContractDeliverable[];
  milestones: ContractMilestone[];
  invoices: ContractInvoice[];
  pdfUrl: string;
  notes: string;
  createdAt: string;
}

export const CONTRACT_STATUS_COLORS: Record<ContractStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  active: 'bg-green-100 text-green-700',
  completed: 'bg-teal-100 text-teal-700',
  terminated: 'bg-red-100 text-red-600',
};

export const DELIVERABLE_STATUS_COLORS: Record<DeliverableStatus, string> = {
  pending: 'bg-slate-100 text-slate-500',
  'in-progress': 'bg-teal-100 text-teal-700',
  completed: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-600',
};

export const MILESTONE_STATUS_COLORS: Record<MilestonePayStatus, string> = {
  upcoming: 'bg-slate-100 text-slate-500',
  reached: 'bg-green-100 text-green-700',
  missed: 'bg-red-100 text-red-600',
};

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: 'bg-slate-100 text-slate-500',
  sent: 'bg-teal-100 text-teal-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-600',
};

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  public: 'Public Sector',
  private: 'Private Sector',
  ngo: 'NGO / Foundation',
  international: 'International Org.',
};

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  management: 'Management Consulting',
  policy: 'Policy & Development',
  engineering: 'Engineering & Feasibility',
  other: 'Other',
};

export const mockContracts: Contract[] = [
  {
    id: 'ctr-001',
    projectId: 'proj-009',
    contractNumber: 'ZC-2024-009',
    title: 'Wind Energy Feasibility Study — Aegean Region',
    clientName: 'Green Anatolia Energy',
    clientType: 'private',
    serviceType: 'engineering',
    status: 'active',
    contractValue: 95000,
    currency: 'USD',
    startDate: '2024-06-01',
    endDate: '2025-02-28',
    staffCountRequired: 2,
    notes: 'Contract includes two progress review meetings with client at months 3 and 6. All deliverables subject to client sign-off within 10 working days of submission.',
    pdfUrl: '',
    createdAt: '2024-05-28',
    invoices: [
      { id: 'inv-001-1', invoiceNumber: 'INV-2024-001', issueDate: '2024-06-05', dueDate: '2024-07-05', amount: 38000, currency: 'USD', status: 'paid', pdfUrl: 'invoice_INV-2024-001.pdf', description: 'Mobilisation & Inception Phase' },
      { id: 'inv-001-2', invoiceNumber: 'INV-2024-002', issueDate: '2024-11-05', dueDate: '2024-12-05', amount: 28500, currency: 'USD', status: 'paid', pdfUrl: 'invoice_INV-2024-002.pdf', description: 'Interim Technical Report' },
      { id: 'inv-001-3', invoiceNumber: 'INV-2025-001', issueDate: '2025-02-01', dueDate: '2025-03-01', amount: 28500, currency: 'USD', status: 'sent', pdfUrl: '', description: 'Final Delivery & Close-out' },
    ],
    deliverables: [
      {
        id: 'd-001-1',
        title: 'Inception Report & Methodology',
        description: 'Detailed work plan, methodology note, and data collection approach for the feasibility study.',
        dueDate: '2024-07-15',
        status: 'completed',
      },
      {
        id: 'd-001-2',
        title: 'Wind Resource Assessment',
        description: 'Analysis of wind data, resource maps, and energy yield estimates for the proposed site.',
        dueDate: '2024-09-30',
        status: 'completed',
      },
      {
        id: 'd-001-3',
        title: 'Technical Feasibility Report',
        description: 'Turbine layout, grid connection analysis, civil infrastructure requirements, and CAPEX estimation.',
        dueDate: '2024-11-30',
        status: 'completed',
      },
      {
        id: 'd-001-4',
        title: 'Financial Model & OPEX Analysis',
        description: 'Bankable financial model with sensitivity analysis, OPEX benchmarking, and IRR/NPV projections.',
        dueDate: '2025-01-15',
        status: 'in-progress',
      },
      {
        id: 'd-001-5',
        title: 'Final Feasibility Study Report',
        description: 'Comprehensive final report integrating all sections, investor-ready format.',
        dueDate: '2025-02-20',
        status: 'pending',
      },
    ],
    milestones: [
      {
        id: 'm-001-1',
        title: 'Contract Signing & Mobilisation',
        date: '2024-06-01',
        paymentAmount: 19000,
        status: 'reached',
        description: 'Initial payment upon contract execution.',
      },
      {
        id: 'm-001-2',
        title: 'Inception Report Approved',
        date: '2024-07-31',
        paymentAmount: 19000,
        status: 'reached',
        description: 'Payment upon client approval of inception report.',
      },
      {
        id: 'm-001-3',
        title: 'Interim Technical Report',
        date: '2024-11-01',
        paymentAmount: 28500,
        status: 'reached',
        description: 'Payment upon approval of wind resource and technical sections.',
      },
      {
        id: 'm-001-4',
        title: 'Draft Final Report',
        date: '2025-01-31',
        paymentAmount: 14250,
        status: 'upcoming',
        description: 'Payment upon submission of draft final report.',
      },
      {
        id: 'm-001-5',
        title: 'Final Delivery & Close-out',
        date: '2025-02-28',
        paymentAmount: 14250,
        status: 'upcoming',
        description: 'Final payment upon client sign-off of completed feasibility study.',
      },
    ],
  },
  {
    id: 'ctr-002',
    projectId: 'proj-010',
    contractNumber: 'ZC-2024-010',
    title: 'Strategic Positioning — MENA Market Entry',
    clientName: 'TechStart Ventures (UAE)',
    clientType: 'international',
    serviceType: 'management',
    status: 'active',
    contractValue: 55000,
    currency: 'USD',
    startDate: '2024-03-01',
    endDate: '2024-09-30',
    staffCountRequired: 2,
    notes: 'Remote engagement with bi-weekly video calls. Client may extend scope for Phase 2 implementation support.',
    pdfUrl: '',
    createdAt: '2024-02-20',
    invoices: [
      { id: 'inv-002-1', invoiceNumber: 'INV-2024-003', issueDate: '2024-03-05', dueDate: '2024-04-05', amount: 27500, currency: 'USD', status: 'paid', pdfUrl: 'invoice_INV-2024-003.pdf', description: 'Kick-off & Market Intelligence Phase' },
      { id: 'inv-002-2', invoiceNumber: 'INV-2024-004', issueDate: '2024-08-01', dueDate: '2024-09-01', amount: 27500, currency: 'USD', status: 'sent', pdfUrl: '', description: 'Final Delivery — Go-to-Market Plan' },
    ],
    deliverables: [
      {
        id: 'd-002-1',
        title: 'MENA Market Intelligence Report',
        description: 'Competitor profiling, market sizing, and customer segmentation across 6 MENA markets.',
        dueDate: '2024-04-30',
        status: 'completed',
      },
      {
        id: 'd-002-2',
        title: 'Competitive Positioning Whitepaper',
        description: 'Differentiation strategy, value proposition canvas, and messaging framework.',
        dueDate: '2024-06-15',
        status: 'completed',
      },
      {
        id: 'd-002-3',
        title: 'Partner Ecosystem Map',
        description: 'Potential distribution, technology, and channel partner landscape for MENA.',
        dueDate: '2024-07-31',
        status: 'completed',
      },
      {
        id: 'd-002-4',
        title: 'Go-to-Market Sequencing Plan',
        description: 'Phased market entry roadmap with KPIs, budgets, and risk assessment.',
        dueDate: '2024-09-15',
        status: 'in-progress',
      },
    ],
    milestones: [
      {
        id: 'm-002-1',
        title: 'Project Kick-off Payment',
        date: '2024-03-01',
        paymentAmount: 13750,
        status: 'reached',
        description: '25% upfront upon kick-off.',
      },
      {
        id: 'm-002-2',
        title: 'Market Intelligence Approved',
        date: '2024-05-15',
        paymentAmount: 16500,
        status: 'reached',
        description: '30% upon approval of market intelligence deliverable.',
      },
      {
        id: 'm-002-3',
        title: 'Mid-Project Milestone',
        date: '2024-07-15',
        paymentAmount: 13750,
        status: 'reached',
        description: '25% upon completion of positioning whitepaper and partner map.',
      },
      {
        id: 'm-002-4',
        title: 'Final Delivery',
        date: '2024-09-30',
        paymentAmount: 11000,
        status: 'upcoming',
        description: '20% upon final client sign-off.',
      },
    ],
  },
  {
    id: 'ctr-003',
    projectId: 'proj-003',
    contractNumber: 'ZC-2021-003',
    title: 'Earthquake Risk & Business Continuity Assessment',
    clientName: 'TÜPRAŞ Marmara Refinery',
    clientType: 'private',
    serviceType: 'engineering',
    status: 'completed',
    contractValue: 112000,
    currency: 'USD',
    startDate: '2021-06-01',
    endDate: '2022-03-15',
    staffCountRequired: 2,
    notes: 'Completed engagement. All deliverables accepted. Client requested follow-up study (declined — outside scope).',
    pdfUrl: '',
    createdAt: '2021-05-25',
    invoices: [
      { id: 'inv-003-1', invoiceNumber: 'INV-2021-001', issueDate: '2021-06-05', dueDate: '2021-07-05', amount: 22400, currency: 'USD', status: 'paid', pdfUrl: 'invoice_INV-2021-001.pdf', description: 'Mobilisation Payment' },
      { id: 'inv-003-2', invoiceNumber: 'INV-2021-002', issueDate: '2021-10-01', dueDate: '2021-11-01', amount: 33600, currency: 'USD', status: 'paid', pdfUrl: 'invoice_INV-2021-002.pdf', description: 'Interim Report 1 — Structural Assessment' },
      { id: 'inv-003-3', invoiceNumber: 'INV-2022-001', issueDate: '2022-01-10', dueDate: '2022-02-10', amount: 33600, currency: 'USD', status: 'paid', pdfUrl: 'invoice_INV-2022-001.pdf', description: 'Interim Report 2 — Equipment & BCP' },
      { id: 'inv-003-4', invoiceNumber: 'INV-2022-002', issueDate: '2022-03-20', dueDate: '2022-04-20', amount: 22400, currency: 'USD', status: 'paid', pdfUrl: 'invoice_INV-2022-002.pdf', description: 'Final Delivery & Close-out' },
    ],
    deliverables: [
      {
        id: 'd-003-1',
        title: 'Structural Vulnerability Assessment',
        description: 'TBDY-2018 and Eurocode 8 compliance review for all critical structures.',
        dueDate: '2021-09-30',
        status: 'completed',
      },
      {
        id: 'd-003-2',
        title: 'Process Equipment Seismic Performance Review',
        description: 'Seismic fragility analysis for key refinery process units.',
        dueDate: '2021-12-15',
        status: 'completed',
      },
      {
        id: 'd-003-3',
        title: 'Business Continuity Exposure Report',
        description: 'Financial exposure modelling and business continuity gap analysis.',
        dueDate: '2022-02-01',
        status: 'completed',
      },
      {
        id: 'd-003-4',
        title: 'Prioritised Action Catalogue',
        description: 'Risk-ranked remediation actions with cost estimates and implementation timeline.',
        dueDate: '2022-03-10',
        status: 'completed',
      },
    ],
    milestones: [
      {
        id: 'm-003-1', title: 'Mobilisation', date: '2021-06-01', paymentAmount: 22400, status: 'reached', description: 'Kick-off payment.',
      },
      {
        id: 'm-003-2', title: 'Interim Report 1', date: '2021-09-30', paymentAmount: 33600, status: 'reached', description: 'Structural assessment complete.',
      },
      {
        id: 'm-003-3', title: 'Interim Report 2', date: '2021-12-31', paymentAmount: 33600, status: 'reached', description: 'Equipment & BCP sections complete.',
      },
      {
        id: 'm-003-4', title: 'Final Delivery', date: '2022-03-15', paymentAmount: 22400, status: 'reached', description: 'Final report accepted.',
      },
    ],
  },
];
